import React, { useState } from 'react';
import {
    Box,
    Button,
    Form,
    FormField,
    Heading,
    Layer,
    PageContent,
    Text,
    TextArea,
    TextInput,
} from 'grommet';
import { Close, Edit } from 'grommet-icons';
import PropTypes from 'prop-types';
import { API } from '@enrich/shared-lib';

export const ContactNotes = ({ contactId, notes = [], revalidate }) => {
    const [showModal, setShowModal] = useState();
    const [editingNote, setEditingNote] = useState({});

    const handleAddNote = () => {
        setShowModal(true);
    };

    const handleSaveNote = async (note) => {
        if (editingNote.id) {
            editNote(note);
        } else {
            createNote(note);
        }
    };

    const createNote = async (note) => {
        try {
            await API.post('/api/contact-notes', {
                data: {
                    contact: contactId,
                    message: note.message,
                    subject: note.subject,
                },
            });
            setShowModal(false);
            revalidate();
        } catch (error) {
            console.log(error);
        }
    };

    const editNote = async (note) => {
        try {
            await API.put(`/api/contact-notes/${note.id}`, {
                data: {
                    message: note.message,
                    subject: note.subject,
                },
            });
            setEditingNote();
            setShowModal(false);
            revalidate();
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <PageContent>
            <Box direction="row-responsive" align="center" justify="between">
                <Box>
                    <Heading level="3">Notes</Heading>
                </Box>

                <Box>
                    <Button
                        primary
                        size="large"
                        label="Add Note"
                        onClick={handleAddNote}
                    />
                </Box>
            </Box>

            <Box gap="small" pad="small">
                {showModal && (
                    <NoteModal
                        editingNote={editingNote}
                        saveNote={handleSaveNote}
                        setShowModal={setShowModal}
                    />
                )}
                {notes
                    .sort(
                        (a, b) =>
                            b.attributes.createdAt > a.attributes.createdAt,
                    )
                    .map((note) => (
                        <ContactNote
                            key={note.id}
                            note={note}
                            openEditNote={(n) => {
                                setEditingNote(n);
                                setShowModal(true);
                            }}
                        />
                    ))}
            </Box>
        </PageContent>
    );
};

const ContactNote = ({ note, openEditNote }) => {
    const formattedCreatedDate = new Date(
        note.attributes.createdAt,
    ).toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    });

    return (
        <Box gap="xsmall">
            <Box
                pad="small"
                border={{ color: 'enrich-green', size: '2px' }}
                elevation="xsmall"
                style={{ whiteSpace: 'pre-wrap' }}
                round="small">
                <Box direction="row" justify="between">
                    <Text weight="bold">{note.attributes.subject}</Text>
                    <Box align="end">
                        <Edit
                            onClick={() => openEditNote(note)}
                            cursor="pointer"
                        />
                    </Box>
                </Box>
                <Box>{note.attributes.message}</Box>
            </Box>
            <Box>
                <Text size="xsmall" color="grey" alignSelf="end">
                    {formattedCreatedDate}
                </Text>
            </Box>
        </Box>
    );
};

const NoteModal = ({ editingNote, saveNote, setShowModal }) => {
    const modalHeading = editingNote.id ? 'Edit Note' : 'Create Note';
    const [noteValue, setNoteValue] = useState({
        message: '',
        subject: '',
        ...editingNote.attributes,
        id: editingNote.id,
    });

    return (
        <Layer
            onClickOutside={() => setShowModal(false)}
            onEsc={() => setShowModal(false)}
            position="center">
            <Box
                background="white"
                pad="medium"
                width="large"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                alignSelf="center"
                gap="30px">
                <Box direction="row" gap="20px" justify="between">
                    <Heading level="3" margin={{ vertical: 'none' }}>
                        {modalHeading}
                    </Heading>
                    <Box align="end">
                        <Close
                            onClick={() => {
                                setShowModal(false);
                            }}
                            cursor="pointer"
                        />
                    </Box>
                </Box>
                <Box>
                    <Form
                        value={noteValue}
                        onChange={(nextValue) => setNoteValue(nextValue)}
                        onReset={() => setNoteValue({})}
                        onSubmit={({ value }) => {
                            saveNote(value);
                        }}>
                        <Box gap="medium">
                            <FormField label="Subject" name="subject">
                                <TextInput name="subject" />
                            </FormField>
                            <FormField label="Message" name="message">
                                <TextArea name="message" />
                            </FormField>
                            <Box align="end">
                                <Button
                                    type="submit"
                                    primary
                                    label="Save"
                                    size="large"
                                />
                            </Box>
                        </Box>
                    </Form>
                </Box>
            </Box>
        </Layer>
    );
};

ContactNotes.propTypes = {
    contactId: PropTypes.string,
    notes: PropTypes.array,
    revalidate: PropTypes.func,
};

ContactNote.propTypes = {
    note: PropTypes.shape({
        id: PropTypes.string,
        attributes: PropTypes.shape({
            createdAt: PropTypes.string,
            message: PropTypes.string,
            subject: PropTypes.string,
        }),
    }),
    openEditNote: PropTypes.func,
};

NoteModal.propTypes = {
    editingNote: PropTypes.shape({
        id: PropTypes.string,
        attributes: PropTypes.shape({
            createdAt: PropTypes.string,
            message: PropTypes.string,
            subject: PropTypes.string,
        }),
    }),
    saveNote: PropTypes.func,
    setShowModal: PropTypes.func,
};
